/**
 * Diagnostic — bloc de recommandation (sections/custom-diagnostic-recommandation).
 *
 * La page de reco est appelée avec le résultat du parcours dans l'URL :
 *
 *   /pages/ma-recommandation?reco=diagnostic-bureau-dynamic&need=bureau&budget=550
 *
 * Liquid ne voit pas ces paramètres. C'est donc ici qu'on traduit `reco` en
 * handles produit — via `window.DiagnosticEngine.CATALOG`, la table qui a
 * servi à calculer la reco, jamais recopiée — puis qu'on va chercher la fiche
 * de chaque produit par la Section Rendering API.
 *
 * Une fois les fiches en place, ce fichier ne suit qu'une chose : la valeur de
 * `input[name="id"]` dans chaque fiche. Que la variante ait été choisie par
 * product-parts.js (bureaux, SKU recomposé) ou par le sélecteur compact des
 * chaises, le prix, le visuel et le total de l'encart en découlent.
 */

if (!customElements.get('diagnostic-reco')) {
  /*
   * Descripteurs des deux sélecteurs compacts : la fiche et son complément ont
   * le même balisage, seul le préfixe des attributs change (ils vivent dans le
   * même bloc, `setupCard` le balaie entier — les attributs ne peuvent pas se
   * confondre). Tout le reste — disponibilité, recomposition — est commun.
   */
  const CARD_PICKER = {
    option: '[data-reco-option]',
    value: '[data-reco-value]',
    key: 'recoValue'
  };

  const CROSS_PICKER = {
    option: '[data-reco-cross-option]',
    value: '[data-reco-cross-value]',
    key: 'recoCrossValue'
  };

  class DiagnosticReco extends HTMLElement {
    connectedCallback() {
      this.cardsEl = this.querySelector('[data-reco-cards]');
      this.mainEl = this.querySelector('[data-reco-main]');
      this.emptyEl = this.querySelector('[data-reco-empty]');
      this.asideEl = this.querySelector('[data-reco-aside]');
      this.titleEl = this.querySelector('[data-reco-title]');
      this.textEl = this.querySelector('[data-reco-text]');
      this.discountEl = this.querySelector('[data-reco-discount]');
      this.submitBtn = this.querySelector('[data-reco-submit]');
      this.totalEl = this.querySelector('[data-reco-total]');
      this.totalCompareEl = this.querySelector('[data-reco-total-compare]');
      this.detailsEl = this.querySelector('[data-reco-details]');
      this.detailEls = [...this.querySelectorAll('[data-reco-detail]')];

      this.fragmentSection = this.dataset.fragmentSection;
      this.discountAmount = Number(this.dataset.discountAmount || 0) * 100;
      this.discountThreshold = Number(this.dataset.discountThreshold || 0) * 100;

      /** @type {Array<{el: HTMLElement, data: object, input: HTMLInputElement}>} */
      this.cards = [];
      this.submitLabel = this.submitBtn
        ? this.submitBtn.querySelector('.reco-aside__cta-label').textContent.trim()
        : '';

      if (this.submitBtn) this.submitBtn.addEventListener('click', this.submit.bind(this));

      this.syncStickyOffset();
      this.boundSyncStickyOffset = this.syncStickyOffset.bind(this);
      window.addEventListener('resize', this.boundSyncStickyOffset);
      // Les polices et la bande d'annonce peuvent encore changer la hauteur de
      // l'en-tête après le premier rendu.
      window.addEventListener('load', this.boundSyncStickyOffset);

      this.watchAsideHeight();
      this.watchDock();

      this.load();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundSyncStickyOffset);
      window.removeEventListener('load', this.boundSyncStickyOffset);

      if (this.asideResizeObserver) this.asideResizeObserver.disconnect();
      else window.removeEventListener('resize', this.boundSyncAsideHeight);

      if (this.dockObserver) this.dockObserver.disconnect();

      document.documentElement.style.removeProperty('--reco-aside-height');
    }

    /**
     * Accroche l'encart en bas de page une fois le bloc principal dépassé.
     *
     * En desktop l'encart est collant, mais sa cellule de grille s'arrête avec
     * le bloc principal : passé celui-ci, il s'en va avec lui. Le reste de la
     * page — détail des recos, sections suivantes — se parcourrait alors sans
     * bouton de commande. La feuille de style le remet à plat en barre.
     *
     * Sous 750 px la question ne se pose pas, l'encart y est déjà fixé en bas ;
     * la classe est posée quand même, mais aucune règle ne la lit.
     */
    watchDock() {
      if (!this.asideEl || !this.mainEl || !('IntersectionObserver' in window)) return;

      this.dockObserver = new IntersectionObserver(
        ([entry]) => {
          // Le bloc est hors champ dans les deux sens ; `bottom <= 0` distingue
          // « déjà dépassé » de « pas encore atteint ».
          const passed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;

          this.asideEl.classList.toggle('reco-aside--docked', passed);
          this.syncAsideHeight();
        },
        { threshold: 0 }
      );

      this.dockObserver.observe(this.mainEl);
    }

    /**
     * Suit la hauteur de l'encart et l'écrit dans `--reco-aside-height`.
     *
     * En mobile l'encart est fixé au bas de l'écran : il ne pousse plus rien,
     * et sans réserve équivalente au bas du document le pied de page reste
     * définitivement sous la barre. La hauteur n'est pas figée dans la feuille
     * de style parce qu'elle bouge — la ligne de remise n'apparaît qu'au-delà
     * du seuil, les textes de l'encart sont réglables, et les polices peuvent
     * arriver après le premier rendu. Un ResizeObserver couvre les trois cas
     * d'un coup ; le repli sur `resize` ne rate que les changements de contenu,
     * déjà suivis par les appels explicites de `refresh` et `showEmpty`.
     */
    watchAsideHeight() {
      if (!this.asideEl) return;

      this.boundSyncAsideHeight = this.syncAsideHeight.bind(this);

      if ('ResizeObserver' in window) {
        this.asideResizeObserver = new ResizeObserver(this.boundSyncAsideHeight);
        this.asideResizeObserver.observe(this.asideEl);
      } else {
        window.addEventListener('resize', this.boundSyncAsideHeight);
      }

      this.syncAsideHeight();
    }

    /** Écrit la hauteur courante de l'encart, 0 s'il est masqué. */
    syncAsideHeight() {
      if (!this.asideEl) return;

      const height = this.asideEl.hidden ? 0 : this.asideEl.offsetHeight;
      document.documentElement.style.setProperty('--reco-aside-height', `${height}px`);
    }

    /**
     * Hauteur réellement occupée en haut de fenêtre par l'habillage collant
     * (en-tête, bandeaux du groupe d'en-tête), écrite dans `--reco-sticky-chrome`
     * pour que l'encart s'arrête juste en dessous.
     *
     * Elle est mesurée et non lue dans `--theme-sticky-header-height` : cette
     * variable du thème n'est pas alimentée sur cette page, et l'encart passait
     * donc sous l'en-tête.
     */
    syncStickyOffset() {
      const main = document.getElementById('content');
      let chrome = 0;

      // Le groupe d'en-tête est rendu en enfant direct de <body>, avant <main>.
      [...document.body.children].forEach((el) => {
        if (
          main &&
          // eslint-disable-next-line no-bitwise
          main.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
        ) {
          return;
        }

        const style = getComputedStyle(el);
        if (style.position !== 'sticky' && style.position !== 'fixed') return;

        // Hauteur épinglée : le décalage d'accroche plus la hauteur du bloc.
        // Les éléments collants se superposent au lieu de s'empiler, d'où le max.
        chrome = Math.max(chrome, (parseFloat(style.top) || 0) + el.getBoundingClientRect().height);
      });

      this.style.setProperty('--reco-sticky-chrome', `${Math.round(chrome)}px`);
    }

    /**
     * Le moteur est chargé en `defer` juste avant ce fichier, mais la page de
     * reco peut aussi être atteinte par le cache du navigateur (retour
     * arrière) : on attend son arrivée plutôt que de partir sans table.
     * @returns {Promise<?object>}
     */
    async catalog() {
      for (let i = 0; i < 40 && !window.DiagnosticEngine; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      return window.DiagnosticEngine ? window.DiagnosticEngine.CATALOG : null;
    }

    /**
     * Handles produit à afficher, dans l'ordre de la recommandation.
     * @param {object} catalog - window.DiagnosticEngine.CATALOG.
     * @returns {string[]}
     */
    handles(catalog) {
      const reco = new URLSearchParams(window.location.search).get('reco');
      if (!reco || !catalog) return [];

      // `diagnostic-duo-lift-terrana` → besoin + slugs.
      const parts = reco.split('-');
      if (parts.shift() !== 'diagnostic' || !parts.length) return [];
      parts.shift();

      const bySlug = {};
      Object.keys(catalog).forEach((key) => {
        bySlug[catalog[key].slug] = catalog[key].handle;
      });

      return this.splitSlugs(parts.join('-'), Object.keys(bySlug)).map((slug) => bySlug[slug]);
    }

    /**
     * Découpe la fin d'un slug de reco en slugs catalogue.
     *
     * Un simple `split('-')` ne suffit pas : les slugs en contiennent
     * eux-mêmes (`terrana-premium-cuir`). On teste donc les plus longs
     * d'abord, et on revient en arrière si la suite ne tombe pas juste.
     *
     * @param {string} rest - Slugs concaténés (`lift-terrana-premium`).
     * @param {string[]} slugs - Slugs connus du catalogue.
     * @returns {string[]} Vide si la chaîne ne se découpe pas proprement.
     */
    splitSlugs(rest, slugs) {
      const sorted = [...slugs].sort((a, b) => b.length - a.length);

      const walk = (value) => {
        if (!value) return [];

        for (let i = 0; i < sorted.length; i += 1) {
          const slug = sorted[i];
          if (value === slug) return [slug];

          if (value.startsWith(`${slug}-`)) {
            const tail = walk(value.slice(slug.length + 1));
            if (tail) return [slug, ...tail];
          }
        }

        return null;
      };

      return walk(rest) || [];
    }

    /** Résout la reco, charge les fiches, branche l'encart. */
    async load() {
      const catalog = await this.catalog();
      let handles = this.handles(catalog);

      if (!handles.length && this.dataset.fallbackHandle) {
        handles = [this.dataset.fallbackHandle];
      }

      if (!handles.length) {
        this.showEmpty();
        return;
      }

      const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
      const found = cards.filter(Boolean);

      if (!found.length) {
        this.showEmpty();
        return;
      }

      this.cardsEl.innerHTML = '';
      found.forEach((card) => this.cardsEl.appendChild(card));
      found.forEach((card) => this.setupCard(card));

      this.applyHeading();
      this.applyDetails();
      this.refresh();

      // Le widget d'avis surveille le DOM et se recharge de lui-même ; l'appel
      // explicite couvre le cas où les fiches arrivent avant que cet
      // observateur soit en place (le script est chargé au `load` de la page).
      if (typeof window.reloadGRCDynamicWidgets === 'function') {
        window.reloadGRCDynamicWidgets('product-summary');
      }
    }

    /**
     * Fiche d'un produit, rendue par sections/diagnostic-reco-produit.
     * @param {string} handle - Handle produit.
     * @returns {Promise<?HTMLElement>}
     */
    async fetchCard(handle) {
      // `routes.root` plutôt qu'un `/` en dur : l'URL reste valide si la
      // boutique sert un jour une locale préfixée.
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      try {
        const response = await fetch(`${root}products/${handle}?section_id=${this.fragmentSection}`);
        if (!response.ok) return null;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Le bloc, pas la seule fiche : il porte aussi le complément.
        return doc.querySelector('[data-reco-item]');
      } catch (error) {
        // Réseau coupé : l'écran de repli vaut mieux qu'une page à moitié rendue.
        return null;
      }
    }

    /** Aucune reco exploitable : on efface le décor et on propose le parcours. */
    showEmpty() {
      this.cardsEl.innerHTML = '';
      if (this.emptyEl) this.emptyEl.hidden = false;
      if (this.asideEl) this.asideEl.hidden = true;
      // Sans fiche, `applyDetails` ne trouve rien à écrire et masque tout.
      this.applyDetails();
      this.syncAsideHeight();
    }

    /**
     * Branche une fiche : lecture de ses données, écoute de sa variante.
     * @param {HTMLElement} el - Élément [data-reco-card].
     * @param {number} [position] - Rang à occuper, pour qu'une fiche
     *   réinitialisée reprenne sa place dans un duo.
     */
    setupCard(el, position) {
      let data = null;

      try {
        data = JSON.parse(el.querySelector('[data-reco-data]').textContent);
      } catch (error) {
        return;
      }

      const input = el.querySelector('input[name="id"]');
      if (!input) return;

      const card = { el, data, input };

      if (typeof position === 'number') this.cards.splice(position, 0, card);
      else this.cards.push(card);

      // product-parts.js écrit la variante recomposée puis émet `change` ;
      // le sélecteur compact ci-dessous fait pareil.
      input.addEventListener('change', () => this.updateCard(card));

      // `on:variant:change` couvre en plus la sélection initiale des bureaux,
      // que product-parts.js rejoue à l'apparition de la fiche.
      el.addEventListener('on:variant:change', () => this.updateCard(card));

      el.querySelectorAll('[data-reco-option]').forEach((option) => {
        option.addEventListener('click', (event) => this.handleOptionClick(card, option, event));
      });

      const reset = el.querySelector('[data-reco-reset]');
      if (reset) reset.addEventListener('click', () => this.reset(card));

      this.setupCross(card);
      this.updateCard(card);
    }

    /**
     * Branche le complément d'une fiche, s'il y en a un.
     *
     * Il est retenu d'emblée — c'est une option intégrée par défaut, que le
     * client retire s'il n'en veut pas. Sa variante est suivie ici seule, sans
     * `input[name="id"]` : contrairement à la fiche, aucun script du thème ne
     * vient écrire dedans, et l'ajout au panier part de l'encart.
     *
     * @param {object} card - Fiche à laquelle le complément est rattaché.
     */
    setupCross(card) {
      const el = card.el.querySelector('[data-reco-cross]');
      if (!el) return;

      let data = null;

      try {
        data = JSON.parse(el.querySelector('[data-reco-cross-data]').textContent);
      } catch (error) {
        return;
      }

      card.cross = { el, data, variant: null, removed: false };

      el.querySelectorAll('[data-reco-cross-option]').forEach((option) => {
        option.addEventListener('click', (event) => this.handleCrossClick(card, option, event));
      });

      const remove = el.querySelector('[data-reco-cross-remove]');
      if (remove) remove.addEventListener('click', () => this.removeCross(card));

      this.updateCross(card);
    }

    /**
     * Sélection d'une valeur d'option sur un complément.
     * @param {object} card - Fiche concernée.
     * @param {HTMLElement} option - Bloc [data-reco-cross-option].
     * @param {MouseEvent} event - Clic d'origine.
     */
    handleCrossClick(card, option, event) {
      const button = event.target.closest(CROSS_PICKER.value);
      if (!button || !option.contains(button) || button.hidden) return;

      option.querySelectorAll(CROSS_PICKER.value).forEach((el) => {
        el.setAttribute('aria-selected', el === button ? 'true' : 'false');
      });

      const position = [...card.cross.el.querySelectorAll(CROSS_PICKER.option)].indexOf(option);
      DiagnosticReco.reconcile(card.cross.el, card.cross.data.variants, CROSS_PICKER, position);

      this.updateCross(card);
    }

    /**
     * Répercute la sélection du complément : variante retenue, prix, visuel.
     *
     * Une combinaison épuisée sort du total et du panier sans masquer le bloc :
     * le client voit son choix, son prix, et la mention de rupture. Seule une
     * combinaison qui n'existe pas laisse le prix vide — il n'y a rien à
     * afficher, le temps qu'il en change.
     *
     * @param {object} card - Fiche concernée.
     */
    updateCross(card) {
      const cross = card.cross;
      if (!cross) return;

      DiagnosticReco.syncAvailability(cross.el, cross.data.variants, CROSS_PICKER);

      const selected = DiagnosticReco.selection(
        [...cross.el.querySelectorAll(CROSS_PICKER.option)],
        CROSS_PICKER
      );

      let variant = null;

      if (!selected.length) {
        // Produit sans sélecteur : la seule variante commandable fait l'affaire.
        variant = cross.data.variants.find((item) => item.available) || null;
      } else if (selected.every((value) => value !== null)) {
        variant = cross.data.variants.find(
          (item) =>
            Array.isArray(item.options) &&
            item.options.length === selected.length &&
            item.options.every((value, index) => value === selected[index])
        ) || null;
      }

      cross.variant = variant && variant.available ? variant : null;

      // Le prix est celui de la combinaison choisie, commandable ou non : une
      // variante épuisée reste un produit, avec son prix. C'est `cross.variant`
      // — vide dès que la variante n'est pas commandable — qui la tient hors du
      // total et du panier, et la mention de rupture qui le dit.
      const priceEl = cross.el.querySelector('[data-reco-cross-price]');
      if (priceEl) priceEl.textContent = variant ? this.formatMoney(variant.price) : '';

      const stockEl = cross.el.querySelector('[data-reco-cross-stock]');
      if (stockEl) stockEl.hidden = !variant || variant.available === true;

      const imageEl = cross.el.querySelector('[data-reco-cross-image]');
      if (imageEl && variant && variant.image) imageEl.src = variant.image;

      // Rappel de la valeur choisie, comme sur la fiche.
      cross.el.querySelectorAll('[data-reco-cross-option]').forEach((option) => {
        const target = option.querySelector('[data-reco-cross-option-value]');
        const active = option.querySelector('[aria-selected="true"]');
        if (target && active) target.textContent = active.dataset.recoCrossValue;
      });

      this.refresh();
    }

    /**
     * Retrait du complément : le bloc disparaît et son prix quitte le total.
     * @param {object} card - Fiche concernée.
     */
    removeCross(card) {
      if (!card.cross) return;

      card.cross.removed = true;
      card.cross.el.hidden = true;
      this.refresh();
    }

    /**
     * Compléments retenus, dans l'ordre des fiches.
     * @returns {object[]} Variantes commandables des compléments non retirés.
     */
    crossVariants() {
      return this.cards
        .map((card) => card.cross)
        .filter((cross) => cross && !cross.removed && cross.variant)
        .map((cross) => cross.variant);
    }

    /**
     * Sélection d'une valeur d'option sur un produit à variantes simples.
     * @param {object} card - Fiche concernée.
     * @param {HTMLElement} option - Bloc [data-reco-option].
     * @param {MouseEvent} event - Clic d'origine.
     */
    handleOptionClick(card, option, event) {
      const button = event.target.closest(CARD_PICKER.value);
      // `hidden` : une valeur retirée parce qu'elle n'existe pas avec la
      // sélection courante n'est pas cliquable pour autant.
      if (!button || !option.contains(button) || button.hidden) return;

      option.querySelectorAll(CARD_PICKER.value).forEach((el) => {
        el.setAttribute('aria-selected', el === button ? 'true' : 'false');
      });

      const position = [...card.el.querySelectorAll(CARD_PICKER.option)].indexOf(option);
      const variant = DiagnosticReco.reconcile(
        card.el,
        card.data.variants,
        CARD_PICKER,
        position
      );

      if (!variant) {
        // Combinaison inexistante : on garde le choix affiché, l'encart se
        // charge de dire que ce n'est pas commandable.
        this.updateCard(card);
        return;
      }

      card.input.value = variant.id;
      card.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Valeurs retenues dans un bloc de sélecteurs, dans l'ordre des options.
     * @param {HTMLElement[]} options - Blocs d'option.
     * @param {object} picker - Descripteur de sélecteurs.
     * @returns {Array<?string>} `null` là où rien n'est retenu.
     */
    static selection(options, picker) {
      return options.map((option) => {
        const active = option.querySelector(`${picker.value}[aria-selected="true"]`);
        return active ? active.dataset[picker.key] : null;
      });
    }

    /**
     * Répercute la sélection courante sur les valeurs proposées.
     *
     * Une valeur n'est jugée que sur les variantes qui reprennent les valeurs
     * retenues aux options précédentes : sur une chaise dont la version sans
     * repose-pieds n'existe qu'en blanc, choisir « Sans » ne doit plus laisser
     * les cinq coloris à l'écran. Ce qui n'existe pas disparaît, ce qui est
     * seulement épuisé reste, estompé — même distinction que le sélecteur de la
     * fiche produit (variant-picker.js, `is-nonexistent` / `is-unavailable`).
     *
     * Le tri se fait dans le sens de lecture des options, et non contre toutes
     * les autres : la première reste ainsi toujours entière. Juger « Sans » sur
     * le coloris retenu le ferait disparaître dès qu'un autre est choisi, et
     * personne ne saurait que cette version existe.
     *
     * @param {HTMLElement} el - Bloc portant les sélecteurs.
     * @param {object[]} variants - Table des variantes.
     * @param {object} picker - Descripteur de sélecteurs.
     */
    static syncAvailability(el, variants, picker) {
      const options = [...el.querySelectorAll(picker.option)];
      if (!options.length) return;

      const selected = DiagnosticReco.selection(options, picker);

      options.forEach((option, position) => {
        // Variantes encore en lice compte tenu des options déjà réglées au-dessus.
        const reachable = variants.filter(
          (variant) =>
            Array.isArray(variant.options) &&
            selected.every(
              (value, index) =>
                index >= position || value === null || variant.options[index] === value
            )
        );

        option.querySelectorAll(picker.value).forEach((button) => {
          const value = button.dataset[picker.key];
          const matches = reachable.filter((variant) => variant.options[position] === value);
          const available = matches.some((variant) => variant.available);

          button.hidden = matches.length === 0;
          button.classList.toggle('is-unavailable', matches.length > 0 && !available);
          button.dataset.available = String(available);
        });
      });
    }

    /**
     * Aligne les options suivantes sur la valeur qui vient d'être choisie, et
     * rend la variante correspondante.
     *
     * Les valeurs impossibles quittent l'écran, mais une valeur déjà retenue
     * plus bas peut le devenir : passer de « Avec repose-pieds » à « Sans »
     * quand le beige n'existe que sur la première. La fiche bascule alors sur
     * la variante la plus proche — celle qui conserve le plus de valeurs déjà
     * choisies, commandable de préférence — au lieu de rester sans variante,
     * prix figé et bouton « Indisponible ».
     *
     * @param {HTMLElement} el - Bloc portant les sélecteurs.
     * @param {object[]} variants - Table des variantes.
     * @param {object} picker - Descripteur de sélecteurs.
     * @param {number} position - Option qui vient d'être cliquée.
     * @returns {?object} Variante retenue, null si le choix ne mène nulle part.
     */
    static reconcile(el, variants, picker, position) {
      const options = [...el.querySelectorAll(picker.option)];
      const selected = DiagnosticReco.selection(options, picker);
      const value = selected[position];

      let best = null;
      let bestScore = -1;

      variants.forEach((variant) => {
        if (!Array.isArray(variant.options) || variant.options[position] !== value) return;

        // Les options précédentes ne bougent pas : le client vient de choisir
        // en les voyant, ce sont elles qui ont dicté ce qu'on lui proposait.
        const keeps = selected.every(
          (other, index) =>
            index >= position || other === null || variant.options[index] === other
        );
        if (!keeps) return;

        // Puis le plus de valeurs conservées en dessous ; à égalité, une
        // variante commandable passe devant.
        const kept = selected.reduce(
          (total, other, index) =>
            index > position && variant.options[index] === other ? total + 1 : total,
          0
        );
        const score = kept * 2 + (variant.available ? 1 : 0);

        if (score > bestScore) {
          bestScore = score;
          best = variant;
        }
      });

      if (!best) return null;

      options.forEach((option, index) => {
        option.querySelectorAll(picker.value).forEach((button) => {
          button.setAttribute(
            'aria-selected',
            button.dataset[picker.key] === best.options[index] ? 'true' : 'false'
          );
        });
      });

      return best;
    }

    /**
     * Répercute la variante courante sur la fiche : prix, visuel, libellés.
     * @param {object} card - Fiche concernée.
     */
    updateCard(card) {
      const variant = card.data.variants.find(
        (item) => String(item.id) === String(card.input.value)
      );

      card.variant = variant || null;

      const priceEl = card.el.querySelector('[data-reco-card-price]');
      if (priceEl && variant) priceEl.textContent = this.formatMoney(variant.price);

      const imageEl = card.el.querySelector('[data-reco-image]');
      if (imageEl && variant && variant.image) imageEl.src = variant.image;

      // Toutes les variantes n'ont pas toutes les valeurs d'option : ce qui
      // n'existe plus avec la sélection courante sort de l'écran.
      DiagnosticReco.syncAvailability(card.el, card.data.variants, CARD_PICKER);

      // Rappel de la valeur choisie à côté du libellé, comme sur la maquette.
      card.el.querySelectorAll('[data-reco-option]').forEach((option) => {
        const target = option.querySelector('[data-reco-option-value]');
        const active = option.querySelector('[aria-selected="true"]');
        if (target && active) target.textContent = active.dataset.recoValue;
      });

      card.el.querySelectorAll('.option-selector').forEach((selector) => {
        const label = selector.querySelector('.label');
        const active = selector.querySelector('.js-option.is-selected, .js-option[aria-selected="true"]');
        if (!label || !active) return;

        let value = label.querySelector('.reco-option__echo');
        if (!value) {
          value = document.createElement('span');
          value.className = 'reco-option__echo';
          label.appendChild(value);
        }

        value.textContent = active.dataset.value || '';
      });

      this.refresh();
    }

    /**
     * Recharge une fiche dans son état par défaut.
     * Plus sûr que de rejouer les sélections à l'envers : product-parts.js
     * reconstruit lui-même son état à l'insertion.
     * @param {object} card - Fiche concernée.
     */
    async reset(card) {
      const fresh = await this.fetchCard(card.data.handle);
      if (!fresh) return;

      const position = this.cards.indexOf(card);

      card.el.replaceWith(fresh);
      this.cards.splice(position, 1);
      this.setupCard(fresh, position);
      this.refresh();
    }

    /** Titre et texte de la page : métafields du premier produit recommandé. */
    applyHeading() {
      const first = this.cards[0];
      if (!first) return;

      if (this.titleEl && first.data.titre) this.titleEl.textContent = first.data.titre;
      if (this.textEl) this.fillRich(this.textEl, first, 'texte');
    }

    /**
     * Recopie dans `target` le fragment enrichi `key` porté par la fiche.
     *
     * Les métachamps sont en texte enrichi : Shopify les rend en HTML côté
     * Liquid, et la fiche les transporte dans des `<template>`. Cloner leur
     * contenu évite d'avoir à reconstruire ce HTML ici — donc aussi d'avoir à
     * le confier à `innerHTML`.
     *
     * @param {HTMLElement} target - Conteneur à remplir.
     * @param {?object} card - Fiche source.
     * @param {string} key - Valeur de `data-reco-rich`.
     * @returns {boolean} Faux si la fiche ne porte pas ce fragment.
     */
    fillRich(target, card, key) {
      const source = card ? card.el.querySelector(`[data-reco-rich="${key}"]`) : null;
      if (!source) return false;

      target.textContent = '';
      target.appendChild(source.content.cloneNode(true));

      // `metafield_tag` enveloppe sa sortie dans un div. On le retire pour que
      // les paragraphes et les listes deviennent les enfants directs du
      // conteneur : c'est à eux que s'adresse la feuille de style.
      const wrapper = target.querySelector('.metafield-rich_text_field');
      if (wrapper) wrapper.replaceWith(...wrapper.childNodes);

      return true;
    }

    /**
     * Détail des recommandations : un bloc par fiche, dans le même ordre.
     *
     * Les blocs sont posés vides par Liquid — la section de page ne connaît pas
     * sa reco, elle ne peut donc pas lire les métafields du produit. Ils
     * arrivent ici avec la fiche, dans `data-reco-data`.
     *
     * Un bloc dont les deux métafields sont vides reste masqué, comme celui qui
     * n'a pas de produit en face : mieux vaut pas de bloc qu'un bloc à trous.
     * Le conteneur suit, sinon la gouttière de la grille laisserait un blanc
     * pour rien au bas de la reco.
     */
    applyDetails() {
      let shown = 0;

      this.detailEls.forEach((el, index) => {
        const card = this.cards[index];
        let filled = false;

        el.querySelectorAll('[data-reco-detail-block]').forEach((block) => {
          const target = block.querySelector('[data-reco-detail-text]');
          const written = target
            ? this.fillRich(target, card, block.dataset.recoDetailBlock)
            : false;

          block.hidden = !written;
          if (!written) return;

          filled = true;
          this.applyDetailTitle(block, card);
        });

        el.hidden = !filled;
        if (filled) shown += 1;
      });

      if (this.detailsEl) this.detailsEl.hidden = shown === 0;
    }

    /**
     * Titre « Pourquoi » : le motif réglé dans la section, où `{produit}` cède
     * la place à la désignation calculée par la fiche — « ce bureau », « cette
     * chaise ». La section ne peut pas la produire elle-même, elle ignore quel
     * produit elle affiche.
     *
     * Seul ce titre varie — « Les conseils des pros » vaut pour tous, et son
     * bloc n'a pas de `data-reco-detail-title`.
     *
     * @param {HTMLElement} block - Bloc [data-reco-detail-block].
     * @param {?object} card - Fiche correspondante.
     */
    applyDetailTitle(block, card) {
      const heading = block.querySelector('[data-reco-detail-title]');
      if (!heading || !card || !this.detailsEl) return;

      const pattern = this.detailsEl.dataset.whyTitle;
      const designation = card.data.designation;
      if (!pattern || !designation) return;

      heading.textContent = pattern.replace('{produit}', designation);
    }

    /** Total de l'encart, remise, état du bouton. */
    refresh() {
      if (!this.cards.length || !this.submitBtn) return;

      const variants = this.cards.map((card) => card.variant);
      const complete = variants.every(Boolean);
      const orderable = complete && variants.every((variant) => variant.available);

      // Les compléments retenus entrent dans le total, mais ne conditionnent
      // pas la commande : un complément indisponible se retire du calcul, il
      // n'empêche pas d'acheter la reco.
      const extras = this.crossVariants();

      const total = complete
        ? variants.reduce((sum, variant) => sum + variant.price, 0) +
          extras.reduce((sum, variant) => sum + variant.price, 0)
        : 0;

      // La remise automatique de la boutique ne s'applique qu'au-delà du
      // seuil : en dessous, ni prix barré ni ligne de remise.
      const discounted = total >= this.discountThreshold && this.discountAmount > 0;

      if (this.totalEl) {
        this.totalEl.textContent = complete
          ? this.formatMoney(discounted ? total - this.discountAmount : total)
          : '';
      }

      if (this.totalCompareEl) {
        this.totalCompareEl.textContent = discounted ? this.formatMoney(total) : '';
        this.totalCompareEl.hidden = !discounted;
      }

      if (this.discountEl) this.discountEl.hidden = !discounted;

      this.submitBtn.disabled = !orderable;
      this.submitBtn.classList.toggle('is-disabled', !orderable);

      const label = this.submitBtn.querySelector('.reco-aside__cta-label');
      if (label) label.textContent = orderable ? this.submitLabel : 'Indisponible';

      // La ligne de remise vient d'apparaître ou de disparaître : la barre
      // fixée en bas n'a plus la même hauteur, la réserve du document non plus.
      this.syncAsideHeight();
    }

    /**
     * Envoie la sélection au paiement — les deux produits d'un duo en une
     * seule requête, pour ne pas laisser un panier à moitié rempli si la
     * seconde échoue.
     *
     * L'ajout au panier reste le passage obligé côté Shopify, mais ni la page
     * panier ni le tiroir ne s'intercalent : le navigateur file sur le
     * checkout dès la réponse.
     */
    async submit() {
      const items = this.cards
        .filter((card) => card.variant)
        .map((card) => ({ id: card.variant.id, quantity: 1 }))
        .concat(this.crossVariants().map((variant) => ({ id: variant.id, quantity: 1 })));

      if (!items.length) return;

      this.submitBtn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!response.ok) throw new Error('add-to-cart');

        // Le bouton reste désactivé sur son libellé : la navigation prend le
        // relais, le réactiver n'inviterait qu'à un second ajout.
        window.location.href = (window.theme && theme.routes && theme.routes.checkout) || '/checkout';
      } catch (error) {
        this.submitBtn.disabled = false;
        const label = this.submitBtn.querySelector('.reco-aside__cta-label');
        if (label) label.textContent = 'Réessayer';
      }
    }

    /**
     * @param {number} cents - Montant en centimes.
     * @returns {string} Montant sans décimales quand il tombe rond, comme la maquette.
     */
    formatMoney(cents) {
      const amount = Math.max(0, cents) / 100;
      const value = amount % 1 === 0 ? String(amount) : amount.toFixed(2).replace('.', ',');
      return `${value}€`;
    }
  }

  customElements.define('diagnostic-reco', DiagnosticReco);
}
